import { FunctionComponent, ReactNode, useState, useEffect } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

export interface AccordionItem {
	id: string;
	title: string;
	badge?: string;
	content: ReactNode;
}

interface AccordionProps {
	items: AccordionItem[];
	allowMultiple?: boolean;
	className?: string;
	openItemId?: string | null;
	defaultOpenIds?: string[];
}

export const Accordion: FunctionComponent<AccordionProps> = ({
	items,
	allowMultiple = false,
	className,
	openItemId,
	defaultOpenIds,
}) => {
	const [openItems, setOpenItems] = useState<Set<string>>(new Set(defaultOpenIds || []));

	// Handle externally controlled open item
	useEffect(() => {
		if (openItemId) {
			setOpenItems(new Set([openItemId]));
		}
	}, [openItemId]);

	const toggleItem = (id: string) => {
		setOpenItems((prev) => {
			const newSet = new Set(prev);

			if (newSet.has(id)) {
				newSet.delete(id);
			} else {
				if (!allowMultiple) {
					newSet.clear();
				}

				newSet.add(id);
			}

			return newSet;
		});
	};

	return (
		<div className={classNames("flex flex-col", className)}>
			{items.map((item) => {
				const isOpen = openItems.has(item.id);

				return (
					<div key={item.id} className="border-b border-border-light last:border-b-0">
						<button
							className="flex w-full cursor-pointer items-center justify-between py-4 text-left text-sm font-medium text-text hover:text-text-meta focus:outline-none"
							type="button"
							onClick={() => toggleItem(item.id)}
						>
							<span className="flex items-center gap-2 pr-4">
								{item.title}
								{item.badge && (
									<span className="inline-flex items-center justify-center rounded-full bg-blue-action px-2 py-0.5 text-xs font-medium text-white">
										{item.badge}
									</span>
								)}
							</span>
							<Icon
								className="h-4 w-4 flex-shrink-0 text-text-meta"
								icon={isOpen ? faChevronUp : faChevronDown}
							/>
						</button>
						{isOpen && <div className="pb-4">{item.content}</div>}
					</div>
				);
			})}
		</div>
	);
};
